import React, { useEffect, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Table, Spinner } from "reactstrap"
import _ from "lodash"

import * as backend from "../../helpers/backend_helper"
import Toaster from "../../components/Common/Toaster"

// Super-admin role editor: set a user's organization-level role and their
// per-factory access (Admin / Edit / View / No access). Complements the Factory
// → Teams tab (which sets granular per-report permissions).

// Organization role is Admin vs Member only — the real per-report access is set
// per factory below, so a separate org-level "Edit/View" would be identical to
// "Member" and only confuse. "Member" is stored as the VIEW permission.
const ORG_LEVELS = [
  { v: "ADMIN", l: "Admin" },
  { v: "VIEW", l: "Member" },
]
const ORG_ROLE_DESC: { [k: string]: string } = {
  ADMIN: "Full control of the organization: add & edit users, and manage every factory — configuration, teams, and report validation.",
  VIEW: "A normal organization member (not an org manager). Cannot manage users or factory configuration; their access to each factory is set below.",
}
const FAC_LEVELS = [
  { v: "NONE", l: "No access" },
  { v: "VIEW", l: "View" },
  { v: "VALIDATE", l: "View & Validate" },
  { v: "EDIT", l: "Edit" },
  { v: "ADMIN", l: "Admin" },
]
// What each per-factory access level grants.
const FAC_LEVEL_DESC: { [k: string]: string } = {
  NONE: "No access to this factory.",
  VIEW: "Member of this factory; specific report permissions are assigned per report in Factory → Teams.",
  VALIDATE: "Can view all of this factory's reports and validate/reject the ones assigned to them (Factory → Validation). Cannot edit shared reports; can only submit their own Daily Work report.",
  EDIT: "Can enter and edit all of this factory's reports.",
  ADMIN: "Can configure this factory (departments, forms, teams, report scoring) and validate/reject reports.",
}

// Reduce a stored permission array to a single level for the select.
const levelOf = (perm: string[]): string => {
  const p = perm || []
  if (p.includes("ADMIN")) return "ADMIN"
  if (p.includes("EDIT")) return "EDIT"
  if (p.includes("VALIDATE")) return "VALIDATE"
  if (p.includes("VIEW")) return "VIEW"
  return "NONE"
}

const ManageAccessModal = (props: any) => {
  const { isOpen, toggle, orgId, user } = props
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [factories, setFactories] = useState<any[]>([])
  const [orgRole, setOrgRole] = useState("VIEW")
  const [facLevels, setFacLevels] = useState<{ [facId: string]: string }>({})

  useEffect(() => {
    if (!isOpen || !user) return
    setLoading(true)
    Promise.all([
      backend.getUserRoles(orgId, user._id),
      backend.getFactory(orgId),
    ])
      .then(([roleData, facs]: any[]) => {
        // Admin stays Admin; everything else (Edit/View/none) is a Member (VIEW).
        setOrgRole((roleData && (roleData.org || []).includes("ADMIN")) ? "ADMIN" : "VIEW")
        const facList = Array.isArray(facs) ? facs : []
        setFactories(facList)
        const levels: { [k: string]: string } = {}
        facList.forEach((f: any) => {
          const found = _.find((roleData && roleData.factories) || [], (r: any) => String(r.facId) === String(f._id))
          levels[f._id] = found ? levelOf(found.permission) : "NONE"
        })
        setFacLevels(levels)
      })
      .catch((e: any) => Toaster.error(typeof e === "string" ? e : "Could not load access"))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user && user._id, orgId])

  const save = async () => {
    setSaving(true)
    try {
      const payload = {
        org: [orgRole],
        factories: factories.map((f: any) => ({
          facId: f._id,
          permission: facLevels[f._id] && facLevels[f._id] !== "NONE" ? [facLevels[f._id]] : [],
        })),
      }
      await backend.setUserRoles(orgId, user._id, payload)
      Toaster.success("Access updated")
      props.onSaved && props.onSaved()
      toggle()
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to update access")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Manage Access{user ? ` — ${user.name}` : ""}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <Spinner color="primary" /> <div className="text-muted mt-2">Loading access…</div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>Organization role</label>
              <Input type="select" value={orgRole} onChange={(e) => setOrgRole(e.target.value)} style={{ maxWidth: 260 }}>
                {ORG_LEVELS.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </Input>
              <small className="text-muted d-block mt-1">{ORG_ROLE_DESC[orgRole]}</small>
            </div>

            <label style={{ fontWeight: 600, marginBottom: 6, display: "block" }}>Factory access</label>
            <div className="mb-2" style={{ fontSize: 12, color: "#6b7785", lineHeight: 1.6 }}>
              <div><strong>Admin</strong> — {FAC_LEVEL_DESC.ADMIN}</div>
              <div><strong>Edit</strong> — {FAC_LEVEL_DESC.EDIT} &nbsp;·&nbsp; <strong>View</strong> — {FAC_LEVEL_DESC.VIEW} &nbsp;·&nbsp; <strong>No access</strong> — {FAC_LEVEL_DESC.NONE}</div>
            </div>
            {factories.length === 0 ? (
              <div className="text-muted">No factories in this organization yet.</div>
            ) : (
              <div className="table-responsive">
                <Table className="table table-bordered mb-0" style={{ verticalAlign: "middle" }}>
                  <thead>
                    <tr className="table-light">
                      <th>Factory</th>
                      <th style={{ width: 200 }}>Access level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factories.map((f: any) => (
                      <tr key={f._id}>
                        <td>{f.name}</td>
                        <td>
                          <Input
                            type="select"
                            value={facLevels[f._id] || "NONE"}
                            onChange={(e) => setFacLevels((s) => ({ ...s, [f._id]: e.target.value }))}
                          >
                            {FAC_LEVELS.map((o) => (
                              <option key={o.v} value={o.v}>{o.l}</option>
                            ))}
                          </Input>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            <small className="text-muted d-block mt-2">
              Report-level (view-only) permissions are set per factory in Factory → Teams.
            </small>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>Cancel</Button>
        <Button color="primary" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save Access"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ManageAccessModal
